import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Lock, Unlock, ArrowUpDown, Eye, Users } from 'lucide-react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { format } from 'date-fns';

/**
 * Table displaying team member strike counts for managers
 */
export default function TeamStrikesTable({ teamStrikes, loading }) {
  const [sortBy, setSortBy] = useState('strike_count');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberStrikes, setMemberStrikes] = useState(null);
  const [loadingMember, setLoadingMember] = useState(false);

  // Sort team strikes
  const sortedStrikes = useMemo(() => {
    if (!teamStrikes) return [];
    return [...teamStrikes].sort((a, b) => {
      const aVal = a[sortBy] || 0;
      const bVal = b[sortBy] || 0;
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
    });
  }, [teamStrikes, sortBy, sortDir]);

  // Handle sort toggle
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
  };

  // View member's strike history
  const handleViewMember = async (member) => {
    setSelectedMember(member);
    setLoadingMember(true);

    try {
      const response = await axiosInstance.get(`/users/${member.user_id}/strikes`);
      setMemberStrikes(response.data);
    } catch (error) {
      console.error('Failed to fetch member strikes:', error);
      toast.error('Failed to load strike details');
      setMemberStrikes(null);
    } finally {
      setLoadingMember(false);
    }
  };

  const getStrikeClass = (count) => {
    if (count >= 3) return 'text-red-600 font-bold';
    if (count > 0) return 'text-amber-600 font-medium';
    return 'text-gray-600';
  };

  const getRoleLabel = (role) => {
    const labels = {
      team_leader: 'Team Leader',
      fitness_manager: 'Fitness Manager'
    };
    return labels[role] || role;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-gray-500">
          Loading team strike data...
        </CardContent>
      </Card>
    );
  }

  if (!teamStrikes || teamStrikes.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-gray-500">
          <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="font-medium">No team members found</p>
          <p className="text-sm mt-2">You don't have any team members assigned.</p>
        </CardContent>
      </Card>
    );
  }

  // Summary stats
  const totalStrikes = teamStrikes.reduce((sum, m) => sum + (m.strike_count || 0), 0);
  const atRisk = teamStrikes.filter(m => m.strike_count >= 2).length;
  const locked = teamStrikes.filter(m => m.is_locked).length;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Team Strike Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold">{teamStrikes.length}</div>
              <div className="text-sm text-gray-500">Team Members</div>
            </div>
            <div className={`rounded-lg p-3 text-center ${atRisk > 0 ? 'bg-amber-50' : 'bg-gray-50'}`}>
              <div className={`text-2xl font-bold ${atRisk > 0 ? 'text-amber-600' : ''}`}>{atRisk}</div>
              <div className="text-sm text-gray-500">At Risk (2+ strikes)</div>
            </div>
            <div className={`rounded-lg p-3 text-center ${locked > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
              <div className={`text-2xl font-bold ${locked > 0 ? 'text-red-600' : ''}`}>{locked}</div>
              <div className="text-sm text-gray-500">Locked Accounts</div>
            </div>
          </div>

          {/* Team Table */}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('strike_count')}
                >
                  <div className="flex items-center gap-1">
                    Strikes
                    <ArrowUpDown className="h-4 w-4" />
                  </div>
                </TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedStrikes.map((member) => (
                <TableRow
                  key={member.user_id}
                  className={member.strike_count >= 2 ? 'bg-amber-50/50' : ''}
                >
                  <TableCell className="font-medium">{member.name}</TableCell>
                  <TableCell>
                    <span className="px-2 py-1 text-xs bg-gray-100 rounded">
                      {getRoleLabel(member.role)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <span className={getStrikeClass(member.strike_count)}>
                        {member.strike_count}/{member.max_strikes}
                      </span>
                      {member.strike_count > 0 && (
                        <AlertTriangle
                          className={`h-4 w-4 ${
                            member.strike_count >= 3 ? 'text-red-500' : 'text-amber-500'
                          }`}
                        />
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    {member.is_locked ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded">
                        <Lock className="h-3 w-3" />
                        Locked
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded">
                        <Unlock className="h-3 w-3" />
                        Active
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewMember(member)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Member Strike Detail Modal */}
      <Dialog open={!!selectedMember} onOpenChange={() => setSelectedMember(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Strike History: {selectedMember?.name}</DialogTitle>
          </DialogHeader>

          {loadingMember ? (
            <div className="text-center py-8 text-gray-500">Loading strike data...</div>
          ) : memberStrikes ? (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <span className={`text-2xl font-bold ${getStrikeClass(memberStrikes.strike_count)}`}>
                    {memberStrikes.strike_count}
                  </span>
                  <span className="text-gray-500">/{memberStrikes.max_strikes} strikes</span>
                </div>
                {memberStrikes.is_locked && (
                  <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full font-medium flex items-center gap-1">
                    <Lock className="h-4 w-4" />
                    Account Locked
                  </span>
                )}
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    memberStrikes.strike_count >= 3 ? 'bg-red-500' :
                    memberStrikes.strike_count > 0 ? 'bg-amber-500' : 'bg-green-500'
                  }`}
                  style={{ width: `${Math.min((memberStrikes.strike_count / memberStrikes.max_strikes) * 100, 100)}%` }}
                />
              </div>

              {/* History */}
              <div>
                <h4 className="font-medium mb-2">History</h4>
                {memberStrikes.strike_history?.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {memberStrikes.strike_history.map((strike, i) => (
                      <div key={i} className="p-3 border rounded bg-white">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{strike.date}</span>
                          <span className="text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded">
                            Strike #{memberStrikes.strike_history.length - i}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Rejected by {strike.rejected_by || 'Admin'}
                          {strike.rejected_at && (
                            <> on {format(new Date(strike.rejected_at), 'PPp')}</>
                          )}
                        </div>
                        {strike.notes && (
                          <div className="text-sm text-gray-600 mt-2 p-2 bg-gray-50 rounded">
                            {strike.notes}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No strike history</p>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">Failed to load strike data</div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
